import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, DollarSign, Bot, TrendingUp, RefreshCw, Check, X,
  Clock, Shield, Terminal, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OperationsDashboard - Admin panel for monitoring API usage and earnings
 * Clean, legitimate business monitoring dashboard
 */
export default function OperationsDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const savedKey = localStorage.getItem('ttc_admin_key');
    if (savedKey) {
      setAdminKey(savedKey);
      authenticate(savedKey);
    }
  }, []);

  const authenticate = async (key) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/ops/dashboard`, {
        headers: { 'X-Admin-Key': key }
      });
      setDashboard(response.data);
      setIsAuthenticated(true);
      localStorage.setItem('ttc_admin_key', key);
      toast.success('Access granted');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid admin key');
        localStorage.removeItem('ttc_admin_key');
      }
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    if (!adminKey) return;
    try {
      const response = await axios.get(`${API}/api/ops/dashboard`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  }, [adminKey]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard();
      const interval = setInterval(fetchDashboard, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchDashboard]);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-green-500 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Operations Dashboard</h1>
              <p className="text-zinc-500 mt-2">API Monitoring & Analytics</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 block mb-2">Admin Key</label>
                <input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && authenticate(adminKey)}
                  placeholder="Enter admin key..."
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                />
              </div>

              <Button
                onClick={() => authenticate(adminKey)}
                disabled={loading || !adminKey}
                className="w-full bg-gradient-to-r from-teal-500 to-green-500"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                Access Dashboard
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="ops-dashboard">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-green-500 flex items-center justify-center">
                <Terminal className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Operations Dashboard</h1>
                <p className="text-xs text-zinc-500">API Monitoring</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex gap-1 mt-4">
            {['overview', 'tasks', 'earnings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  activeTab === tab ? 'bg-teal-500/20 text-teal-400' : 'text-zinc-500 hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4">
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                icon={<Activity className="w-5 h-5" />}
                label="Tasks Completed"
                value={dashboard.stats?.total_completed || 0}
                color="blue"
              />
              <StatCard
                icon={<DollarSign className="w-5 h-5" />}
                label="Today's Revenue"
                value={`$${dashboard.today?.revenue?.toFixed(2) || '0.00'}`}
                color="green"
              />
              <StatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="Total Revenue"
                value={`$${dashboard.stats?.total_revenue?.toFixed(2) || '0.00'}`}
                color="purple"
              />
            </div>

            {/* Recent Tasks */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-4">Recent Tasks</h2>
              <div className="space-y-2">
                {dashboard.recent_tasks?.length > 0 ? (
                  dashboard.recent_tasks.map((task, idx) => (
                    <div key={idx} className="p-3 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white">{task.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          task.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1 text-xs text-zinc-500">
                        <span>From: {task.requester_name || 'API'}</span>
                        <span className="text-green-400">${task.actual_price?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-8">No tasks yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
            <DollarSign className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-3xl font-bold text-white">${dashboard?.stats?.total_revenue?.toFixed(2) || '0.00'}</p>
            <p className="text-zinc-500 mt-2">Total Revenue</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    green: 'from-green-500 to-emerald-500',
    blue: 'from-blue-500 to-cyan-500',
    purple: 'from-purple-500 to-pink-500'
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[color]} flex items-center justify-center`}>
          {icon}
        </div>
        <div>
          <p className="text-zinc-500 text-sm">{label}</p>
          <p className="text-xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
